/**
 * Mode helpers & structure (code only).
 *
 * NOTE S7 Phase A : la donnee des modes vit dans la base (source unique), servie via
 * le content-pack. Ce fichier ne garde que le code : helpers et constantes de structure
 * (tiers, difficulte, aliases). Le runtime lit MODES depuis le miroir
 * (src/data/contentStore), re-exporte ci-dessous.
 */

import { MODES, MODE_TO_TIER, MODES_BY_TIER } from '../../data/contentStore';

export { MODES, MODE_TO_TIER, MODES_BY_TIER };

// Legacy aliases for backward compatibility
// These map old names (with ASCII characters) to new names (with Unicode)
export const MODE_ALIASES = {
  'Dorian b2': 'Dorian ♭2',
  'Mixolydian b6': 'Mixolydian ♭6',
  'Dorian b5': 'Dorian ♭5',
  'Phrygian b4': 'Phrygian ♭4',
  'Lydian b3': 'Lydian ♭3',
  'Mixolydian b2': 'Mixolydian ♭2',
  'Locrian bb7': 'Locrian ♭♭7',
  'Locrian bb3 bb7': 'Locrian ♭♭3 ♭♭7',
  'Locrian #6': 'Locrian #6',  // No change needed
  // Add reverse mappings for components that use Unicode
  'Dorian ♭2': 'Dorian ♭2',
  'Mixolydian ♭6': 'Mixolydian ♭6',
  'Dorian ♭5': 'Dorian ♭5',
  'Phrygian ♭4': 'Phrygian ♭4',
  'Lydian ♭3': 'Lydian ♭3',
  'Mixolydian ♭2': 'Mixolydian ♭2',
  'Locrian ♭♭7': 'Locrian ♭♭7',
  'Locrian ♭♭3 ♭♭7': 'Locrian ♭♭3 ♭♭7',
  // Duplicate scale aliases (removed entries → canonical names)
  'Hijaz': 'Phrygian Dominant',
  'Hijaz Kar': 'Double Harmonic',
  'Double Harmonic Minor': 'Hungarian Minor'
};

// ═══════════════════════════════════════════════════════════════════════════
// TIER CLASSIFICATION
// Safe-by-default, fun-by-discovery, geek-on-demand.
// See PLAYGROUND v1 Product Direction for rationale.
// ═══════════════════════════════════════════════════════════════════════════

export const SCALE_TIERS = ['essentials', 'colors', 'wild'];

export const SCALE_TIER_LABELS = {
  essentials: 'Essentials',
  colors: 'Colors',
  wild: 'Wild'
};

export const SCALE_TIER_DESCRIPTIONS = {
  essentials: 'The safe zone. Scales every musician meets first.',
  colors: 'Sophistication, used in jazz, neo-soul, cinematic. Some chords get tense, that is the vibe.',
  wild: 'Use your ears. Exotic, symmetrical, experimental. Here to surprise you.'
};

// NOTE S7 Phase A : l'assignation mode -> tier est de la donnee, en base. MODES_BY_TIER
// et MODE_TO_TIER sont lus depuis le miroir (re-exportes en haut), donc la base est la
// source unique du tiering. Les listes en dur ont disparu du runtime.

/**
 * Get the tier for a given mode name.
 * @param {string} modeName
 * @returns {'essentials' | 'colors' | 'wild' | null}
 */
export const getTierForMode = (modeName) => {
  if (MODE_TO_TIER[modeName]) return MODE_TO_TIER[modeName];
  const canonical = MODE_ALIASES[modeName];
  return canonical ? MODE_TO_TIER[canonical] || null : null;
};

/**
 * Get the tier that a scaleType belongs to.
 * A scaleType belongs to the tier of its first mode.
 * @param {string} scaleType
 * @returns {'essentials' | 'colors' | 'wild' | null}
 */
export const getTierForScaleType = (scaleType) => {
  const firstModeForType = Object.entries(MODES).find(
    ([, data]) => data.scaleType === scaleType
  );
  if (!firstModeForType) return null;
  const [modeName] = firstModeForType;
  return getTierForMode(modeName);
};

// Difficulty of playing a scale over a backing track.
// Easy = fewer notes, all safe (pentatonics/blues). Intermediate = full diatonic modes.
// Advanced = exotic/symmetric ('wild' tier) or 8+ note scales (use your ears).
export const SCALE_DIFFICULTY_ORDER = ['easy', 'intermediate', 'advanced'];

export const SCALE_DIFFICULTY_LABELS = {
  easy: 'Easy',
  intermediate: 'Intermediate',
  advanced: 'Advanced'
};

/**
 * Rate how hard a scale is to improvise over a compatible backing track.
 * @param {string} modeName
 * @returns {'easy' | 'intermediate' | 'advanced'}
 */
export const getScaleDifficulty = (modeName) => {
  const mode = getMode(modeName);
  if (!mode) return 'intermediate';
  const noteCount = mode.intervals.length;
  if (getTierForMode(modeName) === 'wild' || noteCount >= 8) return 'advanced';
  if (noteCount <= 6) return 'easy';
  return 'intermediate';
};

/**
 * Get a mode by name, handling aliases
 * @param {string} modeName - The mode name (may be an alias)
 * @returns {object|null} - The mode data, or null if not found
 */
export const getMode = (modeName) => {
  // Direct lookup
  if (MODES[modeName]) {
    return MODES[modeName];
  }

  // Try alias lookup
  const canonicalName = MODE_ALIASES[modeName];
  if (canonicalName && MODES[canonicalName]) {
    return MODES[canonicalName];
  }

  return null;
};

/**
 * Get all mode names
 * @returns {string[]} - Array of all mode names
 */
export const getAllModeNames = () => Object.keys(MODES);

/**
 * Get mode intervals (for backward compatibility)
 * @param {string} modeName - The mode name
 * @returns {number[]|null} - Array of intervals, or null if not found
 */
export const getModeIntervals = (modeName) => {
  const mode = getMode(modeName);
  return mode ? mode.intervals : null;
};

/**
 * Get mode info for display (description, characteristicNotes, etc.)
 * @param {string} modeName - The mode name
 * @returns {object} - Object with description, characteristicNotes, commonUse, tags
 */
export const getModeInfo = (modeName) => {
  const mode = getMode(modeName);
  if (!mode) {
    return {
      description: 'Explore this unique scale and discover its character.',
      characteristicNotes: 'Varies',
      commonUse: 'Creative exploration and unique compositions.',
      tags: ['Explore', 'Unique', 'Creative']
    };
  }

  return {
    description: mode.description,
    characteristicNotes: mode.characteristicNotes,
    commonUse: mode.commonUse,
    tags: mode.tags || []
  };
};
